/**
 * Subscribes to per-stage document ingestion progress events on the same
 * `ingest_*` Ably channel as `useIngestionImpactFeed`, but a different
 * event name (`Ingestion Progress`) and no debouncing — voice consumers
 * want each stage delivered immediately so Ellie can narrate as the
 * pipeline progresses.
 *
 * Backend publish points are in
 * backend/api/client/controllers/document/process/index.js
 * (publishIngestionProgress helper). Stages today: 'extracted',
 * 'findings'. Failures continue to flow on the existing `documents_*`
 * channel via useDocumentChannel.
 */
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAblyClient } from '../contexts/AblyContext';
import config from '../config';
import type { RootState } from '../store';

export type IngestionProgressStage = 'extracted' | 'findings';

export interface IngestionProgressItemMedication { name: string }
export interface IngestionProgressItemContact { name: string; role?: string | null }
export interface IngestionProgressItemEvent { title: string; start_date?: string | null }

export interface IngestionProgressEvent {
  stage: IngestionProgressStage;
  /** The storage key. Not for reading aloud — a Fasten import's is
   *  `fasten_<uuid>.txt`. */
  filename: string | null;
  document_id: string | number;
  /** What to call the document. Absent on events published before the backend
   *  started sending it, so every consumer falls back to `filename`. */
  display_name?: string | null;
  project_id: number | null;
  ts: number;
  // Stage-specific payload — present at runtime, optional at the type
  // level so callers can switch on `stage` without unsafe access.
  char_count?: number;
  summary?: string | null;
  medications?: number;
  contacts?: number;
  events?: number;
  diagnoses?: number;
  inferences?: number;
  memories?: number;
  proposed_actions?: number;
  // Up to 5 representative items per type (capped server-side) so voice
  // narration can name specifics ("Lisinopril 10mg, Metformin") instead
  // of just counts.
  medication_items?: IngestionProgressItemMedication[];
  contact_items?: IngestionProgressItemContact[];
  event_items?: IngestionProgressItemEvent[];
}

interface Options {
  projectId: number | string | null | undefined;
  onEvent: (event: IngestionProgressEvent) => void;
}

export function useIngestionProgressFeed({ projectId, onEvent }: Options) {
  const { client } = useAblyClient();
  const account = useSelector((s: RootState) => (s as any).session.account);
  const user = useSelector((s: RootState) => s.authentication.user);

  // Stable handler ref so re-rendering parents don't trigger
  // resubscribe loops on Ably (matches the pattern in useDocumentChannel).
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!client || !account?.account_id || !user?.cognito_id || projectId == null) return;

    const channelName = `ingest_${config.env}_${account.account_id}_${user.cognito_id}_${projectId}`;
    const eventName = 'Ingestion Progress';

    let channel: any;
    const handler = (message: any) => {
      const data = message?.data;
      if (!data || typeof data !== 'object' || !data.stage || data.document_id == null) return;
      onEventRef.current(data as IngestionProgressEvent);
    };

    try {
      channel = client.channels.get(channelName);
      channel.subscribe(eventName, handler);
    } catch {
      // Ably may not be ready yet; effect re-runs when deps change.
    }

    return () => {
      try { channel?.unsubscribe(eventName, handler); } catch {}
    };
  }, [client, account?.account_id, user?.cognito_id, String(projectId)]);
}
